/**
 * COMPILE AND PERSIST a generated exam.
 *
 * One function is called from both places an exam source can change — the creation job and the teacher's
 * edit — because everything derived from the source has to be re-derived together. A compile is not just
 * a new PDF: it is a new task tree, new page images, and a new set of positions. Splitting that into
 * "the agent's path" and "the editor's path" is how the two drift.
 *
 *   source (.typ)  ──compile──▶  PDF ──▶ page images (blankExamIds)
 *                  ── query ──▶  structure ──▶ Exam.tasks
 *
 * TYPST IS A DEPENDENCY, NOT A BINARY. `@myriaddreamin/typst-ts-node-compiler` is the typst compiler as a
 * native node addon, delivered as per-platform npm packages through `optionalDependencies` — so `pnpm i`
 * installs it like anything else: nothing is downloaded at install time, there is no PATH to get wrong and
 * nothing to add to the image. Its query output was compared block for block against the release CLI's
 * `typst eval` on the same document and is identical.
 *
 * NOTHING TOUCHES THE FILESYSTEM. The template, the generated source and the figures are handed over as
 * shadow files under a workspace path that does not exist on disk, so a compile has no temp directory to
 * create, race on, or fail to clean up.
 *
 * SERVER ONLY. This module imports node builtins at module scope, so a client file must go through a
 * server-side mutation resolver with a dynamic import — the same seam `exam-creation.job.ts`'s handler
 * uses to reach the creation agent — rather than importing this directly.
 *
 * NOTHING is parsed out of the source text, and nothing is OCR'd off the page. The template emits every
 * task's number, type, points and prompt as metadata (see typst/exam.typ), so querying `<gradias>` returns
 * the structure the document itself declares. A taskType is not recoverable from a printed sheet at all —
 * that is the whole reason a created exam is worth more downstream than an uploaded one.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { NodeCompiler } from '@myriaddreamin/typst-ts-node-compiler';
import { readFile } from 'node:fs/promises';
import * as path from 'node:path';
import { fileURLToPath } from 'node:url';
import { BoundingBox } from '../../BoundingBox.js';
import { AdditionalInfo, Exam, ExamFigureAsset, ExamPlotAsset } from '../../exam.js';
import { Point } from '../../Point.js';
import type { BoundingBoxContentType } from '../../../shared/bounding-box-content.js';
import {
  assembleTaskNodes,
  computeBoundingBoxes,
  validateBlocks,
  type CompileProblem,
  type ExamLayout,
  type GradiasBlock,
} from './exam-structure.js';
import { buildExamTasksFromRecursiveStructure } from '../agents/extraction-modules/build-exam-tasks.js';
import { extractImagesFromPDFMutation } from '../file-intake.js';
import { cropAndSaveRegion } from '../../utils/crop-image.js';
import { TaskType } from '../task-types/task-main.js';

/**
 * Where the house template lives. Every server file is bundled into one `dist/server/index.mjs`, in
 * development as well as in production — there is no layout in which this module sits next to its own
 * `typst/` directory, which is what the first version of this list wrongly assumed. So two real cases:
 *
 *   production — `npm run build` copies the directory to `dist/server/typst/`, beside the bundle.
 *   development — nothing is copied, and the sources are still there: `src/db/api/exam-creation/typst`,
 *                 two levels up from `dist/server/`. Reading them directly is better than a copy step
 *                 anyway, since an edit to `exam.typ` then takes effect without a rebuild.
 *
 * The env override stays first, because a missing template must be fixable without a redeploy, and the
 * error below prints every path it tried rather than guessing at the reason.
 */
const TYPST_DIR_CANDIDATES = [
  ...(process.env.TYPST_TEMPLATE_DIR ? [process.env.TYPST_TEMPLATE_DIR] : []),
  fileURLToPath(new URL('./typst', import.meta.url)),
  fileURLToPath(new URL('../../src/db/api/exam-creation/typst', import.meta.url)),
  // Kept for a build that emits per-file modules instead of one bundle.
  fileURLToPath(new URL('./db/api/exam-creation/typst', import.meta.url)),
];

let typstDir: string | undefined = undefined;

/**
 * Reads a file out of the typst directory — the template itself, or one of the examples the creation
 * agent is shown. Going through one resolver means the agent's few-shot examples are literally the files
 * that get compiled, so its instructions cannot drift from the template it has to call.
 */
export async function readTypstFile(relativePath: string): Promise<string> {
  for (const candidate of typstDir ? [typstDir] : TYPST_DIR_CANDIDATES) {
    try {
      // The candidates are ordered by preference and the first hit is the answer, so probing them in
      // parallel would read files nobody needs.
      // eslint-disable-next-line no-await-in-loop -- sequential by design, see above
      const contents = await readFile(path.join(candidate, relativePath), 'utf8');
      typstDir = candidate;
      return contents;
    } catch {
      continue;
    }
  }
  throw new Error(
    `typst file "${relativePath}" not found — looked in:\n${TYPST_DIR_CANDIDATES.join('\n')}\n` +
      'Set TYPST_TEMPLATE_DIR, or check that the build copies src/db/api/exam-creation/typst to dist/server/typst.',
  );
}

/**
 * The in-memory workspace every compile happens in. An absolute path that is never created: the compiler
 * resolves only what has been registered as a shadow file, so nothing here can reach a real file — a
 * model-written `#include "/etc/passwd"` finds nothing rather than something.
 */
const WORKSPACE_ROOT = '/gradias-exam';
const MAIN_PATH = `${WORKSPACE_ROOT}/main.typ`;

/** The label the template attaches to every metadata block (see typst/exam.typ). */
const METADATA_LABEL = 'gradias';

/** A separate, template-free workspace for standalone snippets (see `compileStandaloneSvg`). */
const SNIPPET_ROOT = '/gradias-snippet';
const SNIPPET_PATH = `${SNIPPET_ROOT}/main.typ`;

// ─── running typst ──────────────────────────────────────────────────────────────────────────────────

/** An image the source references as `/assets/<name>`. */
export interface ExamAsset {
  name: string;
  bytes: Buffer;
}

/**
 * ONE STUDENT'S ANSWER TO ONE TASK, in the shape the template prints it — see `ANSWERS` in exam.typ.
 *
 * Every field is optional and independent: a task can be answered with typed text, a drawing, ticked
 * options, a set of pairings, a set of figure labels, or several of those at once, and whichever are
 * present get printed into the space the blank exam already left for them.
 */
export interface ExamAnswer {
  /** Typed into ruled lines or an answer box. Printed as TEXT, never as markup — see `gradias-written`. */
  text?: string;
  /** A drawing, as PNG bytes. Registered as its own shadow file and printed into the task's answer space. */
  drawing?: Buffer;
  /** `#mc` — which options were ticked, as zero-based indexes into the option list. */
  choice?: number[];
  /** `#match-columns` — left row index (as a string) to every right row index it was joined to (matching
   * allows one row to join more than one on the other side). */
  match?: Record<string, number[]>;
  /** A labelling `#figure-block` — the printed callout number (as a string) to what was written in its box. */
  labels?: Record<string, string>;
  /** `#gap`/`#prompt-list` — the blank's own 1-based printed number to what was written in it. */
  blanks?: Record<string, string>;
}

/** What a submission was answered with, keyed by task number exactly as the source declares it ("1.a"). */
export type ExamAnswers = Record<string, ExamAnswer>;

export interface CompileResult {
  pdf: Buffer;
  /** Rich-content SVG (`compiler.svg`, not `plainSvg`) — the editor's base layer for its hitbox overlay. */
  svg: string;
  blocks: GradiasBlock[];
  /** Every task/material's on-page extent, in points, plus the page size — see `computeBoundingBoxes`. */
  layout: ExamLayout;
  pageCount: number;
  problems: CompileProblem[];
}

export class TypstCompileError extends Error {
  constructor(public readonly diagnostics: string) {
    super(`typst failed: ${diagnostics}`);
    this.name = 'TypstCompileError';
  }
}

// ─── answers, as a typst module ─────────────────────────────────────────────────────────────────────

/** Where a task's drawing is registered, and the path the generated module points the template at. */
function drawingPath(taskNumber: string): string {
  // One file per task, named from its number with everything unusual replaced — a task number is "1.a",
  // and a path is not the place to find out what else a source might have put in one.
  return `/answers/${taskNumber.replace(/[^\w.-]/gu, '_')}.png`;
}

/**
 * A string as a typst string LITERAL.
 *
 * This is the one place a student's own words become part of a program, so it escapes rather than trusts:
 * a backslash or a quote left alone would end the literal early and the rest of the answer would be read
 * as typst code. Control characters go out as escapes for the same reason — the result has to be a single
 * literal on a single line whatever arrived.
 *
 * The template then renders the value as TEXT and never as markup (see `gradias-written`), so even a
 * perfectly-escaped answer full of `#` and `_` prints as itself.
 */
function typstString(value: string): string {
  const escaped = value
    .replace(/\\/gu, '\\\\')
    .replace(/"/gu, '\\"')
    .replace(/\n/gu, '\\n')
    .replace(/\r/gu, '')
    .replace(/\t/gu, '\\t')
    // Anything else non-printable, including the line and paragraph separators a paste can carry.
    .replace(/[\u0000-\u001f\u007f\u2028\u2029]/gu, char => `\\u{${char.codePointAt(0)!.toString(16)}}`);
  return `"${escaped}"`;
}

/** `(a, b, c)` — typst's array literal, with its one-element form (`(a,)`) that would otherwise be a group. */
function typstArray(values: string[]): string {
  return values.length === 1 ? `(${values[0]},)` : `(${values.join(', ')})`;
}

/** `(key: value, ...)` — typst's dict literal, with its empty form (`(:)`) that would otherwise be an array. */
function typstDict(entries: [string, string][]): string {
  if (entries.length === 0) return '(:)';
  return `(${entries.map(([key, value]) => `${typstString(key)}: ${value}`).join(', ')})`;
}

/**
 * The `gradias-answers.typ` the template imports — a plain typst module declaring one dict.
 *
 * GENERATED RATHER THAN EDITED INTO THE SOURCE. The exam's own source is written by the model and saved
 * verbatim; adding an argument to the `#show: exam.with(...)` call in it means parsing a call that may be
 * formatted any number of ways, and a wrong guess there corrupts the document rather than failing. The
 * compiler owns this workspace, so it can simply put another file in it.
 */
function renderAnswersModule(answers: ExamAnswers): string {
  const entries: [string, string][] = [];

  for (const [taskNumber, answer] of Object.entries(answers)) {
    const fields: [string, string][] = [];
    if (answer.text) fields.push(['text', typstString(answer.text)]);
    if (answer.drawing) fields.push(['image', typstString(drawingPath(taskNumber))]);
    if (answer.choice?.length) fields.push(['choice', typstArray(answer.choice.map(index => String(index)))]);
    if (answer.match && Object.keys(answer.match).length > 0) {
      fields.push([
        'match',
        // Each row's value is itself a typst array of right-row indexes, not a bare number — see
        // `ExamAnswer.match` and `match-columns` in exam.typ, which draws one line per entry in it.
        typstDict(Object.entries(answer.match).map(([row, targets]) => [row, typstArray(targets.map(String))])),
      ]);
    }
    if (answer.labels && Object.keys(answer.labels).length > 0) {
      fields.push([
        'labels',
        typstDict(Object.entries(answer.labels).map(([number, label]) => [number, typstString(label)])),
      ]);
    }
    if (answer.blanks && Object.keys(answer.blanks).length > 0) {
      fields.push([
        'blanks',
        typstDict(Object.entries(answer.blanks).map(([number, text]) => [number, typstString(text)])),
      ]);
    }
    // A task answered with nothing at all is left out entirely rather than written as an empty record:
    // the template asks "is there an answer here", and an empty one would say yes.
    if (fields.length > 0) entries.push([taskNumber, typstDict(fields)]);
  }

  return `// Generated per compile — see renderAnswersModule in compile-exam.ts.\n#let ANSWERS = ${typstDict(entries)}\n`;
}

/**
 * Compiles a generated source together with the house template and its figures.
 *
 * THE WORKSPACE IS NOT A DIRECTORY. The template, the source and every asset are registered as shadow
 * files under `WORKSPACE_ROOT`, which never exists on disk. That is also why figure paths are
 * root-relative: typst refuses to read outside the workspace, and a caller-relative path would resolve
 * against the template's own location instead (see the note on `figure-block`).
 *
 * A FRESH COMPILER PER CALL, deliberately. A reused one keeps the previous document's shadow files, so a
 * figure dropped between two compiles would still resolve and a stale template would keep compiling.
 * Measured at about ten milliseconds for a whole exam, which buys that isolation for nothing.
 */
export async function compileExamSource(
  source: string,
  assets: ExamAsset[] = [],
  answers: ExamAnswers = {},
): Promise<CompileResult> {
  const compiler = NodeCompiler.create({ workspace: WORKSPACE_ROOT });
  compiler.addSource(`${WORKSPACE_ROOT}/exam.typ`, await readTypstFile('exam.typ'));
  compiler.addSource(MAIN_PATH, source);
  for (const asset of assets) compiler.mapShadow(`${WORKSPACE_ROOT}/assets/${asset.name}`, asset.bytes);

  // ALWAYS WRITTEN, empty or not: the template imports this file unconditionally, and an import of a file
  // that is sometimes there is a compile that sometimes fails. An empty dict is the blank exam, and the
  // blank exam's output is byte-identical to what it was before answers existed.
  compiler.addSource(`${WORKSPACE_ROOT}/gradias-answers.typ`, renderAnswersModule(answers));
  for (const [taskNumber, answer] of Object.entries(answers)) {
    if (answer.drawing) compiler.mapShadow(`${WORKSPACE_ROOT}${drawingPath(taskNumber)}`, answer.drawing);
  }

  const compiled = compiler.compile({ mainFilePath: MAIN_PATH });
  if (compiled.hasError()) {
    // This string is the repair phase's entire input, so it is formatted the way typst's own CLI reports a
    // failure — severity, file, line, column, message — which is the shape the model has seen before.
    throw new TypstCompileError(formatDiagnostics(compiler, compiled.takeDiagnostics()));
  }

  const document = compiled.result;
  if (!document) throw new TypstCompileError('the compiler returned no document and no diagnostics');

  const pdf = compiler.pdf(document);
  const svg = compiler.svg(document);
  const blocks = readBlocks(compiler.query(document, { selector: `<${METADATA_LABEL}>`, field: 'value' }));
  const layout = computeBoundingBoxes(blocks);

  return {
    pdf: Buffer.from(pdf),
    svg,
    blocks,
    layout,
    pageCount: blocks.reduce((max, block) => Math.max(max, block.pos?.page ?? 0), 0),
    problems: validateBlocks(blocks, layout),
  };
}

/**
 * Renders the compiler's structured diagnostics as the text the repair phase is given.
 *
 * Line and column arrive zero-based and are printed one-based, like every compiler. Warnings are kept
 * alongside errors: when a compile fails they are often the explanation (a missing font, an ignored
 * package) rather than noise.
 */
function formatDiagnostics(compiler: NodeCompiler, diagnostics: unknown, root: string = WORKSPACE_ROOT): string {
  if (!diagnostics) return 'the compile failed without diagnostics';

  const entries = readDiagnostics(compiler, diagnostics);
  if (entries === null) return 'the compile failed and its diagnostics could not be read';

  const lines = entries.map(entry => {
    const file = String(entry?.path ?? '').replace(`${root}/`, '');
    const line = Number(entry?.range?.start?.line);
    const column = Number(entry?.range?.start?.character);
    const place = Number.isFinite(line) ? `${file}:${line + 1}:${column + 1}` : file;
    const severity = entry?.severity === 2 ? 'warning' : 'error';
    return `${severity}: ${place}: ${String(entry?.message ?? 'unknown problem')}`;
  });

  return lines.length > 0 ? lines.join('\n') : 'the compile failed without diagnostics';
}

/** Separated so a compiler that changes its diagnostic shape costs a log line, not a thrown job. */
function readDiagnostics(compiler: NodeCompiler, diagnostics: unknown): any[] | null {
  try {
    return compiler.fetchDiagnostics(diagnostics as never);
  } catch (err) {
    logger.warn({ err }, 'Could not read typst diagnostics');
    return null;
  }
}

/** The query returns whatever the document put in its metadata, so the shape is checked before use. */
function readBlocks(queried: unknown): GradiasBlock[] {
  if (!Array.isArray(queried)) throw new Error('querying the exam metadata did not return an array');
  return queried as GradiasBlock[];
}

/**
 * Compiles a standalone typst document — no exam template, no metadata stream, just markup that renders to
 * one image — and returns it as SVG. What `exam-plots.ts` calls to turn plotting code into a figure: the
 * document owns its own page size, so the plot's bounding box IS the image, with no exam layout to fit it
 * into and nothing to query out of it afterwards.
 *
 * A different workspace than `compileExamSource`'s (`SNIPPET_ROOT`, not `WORKSPACE_ROOT`) — not because
 * anything would break sharing one, but because a snippet compile and an exam compile can run for
 * different exams at the same moment, and giving them a workspace each means neither call has to reason
 * about whether the other one's shadow files could still be attached.
 */
export async function compileStandaloneSvg(source: string): Promise<string> {
  const compiler = NodeCompiler.create({ workspace: SNIPPET_ROOT });
  compiler.addSource(SNIPPET_PATH, source);

  const compiled = compiler.compile({ mainFilePath: SNIPPET_PATH });
  if (compiled.hasError()) {
    throw new TypstCompileError(formatDiagnostics(compiler, compiled.takeDiagnostics(), SNIPPET_ROOT));
  }

  const document = compiled.result;
  if (!document) throw new TypstCompileError('the compiler returned no document and no diagnostics');
  return compiler.plainSvg(document);
}

// ─── persist ────────────────────────────────────────────────────────────────────────────────────────

export interface PersistResult {
  taskCount: number;
  pageCount: number;
  pdfFileId: string;
  blankExamIds: string[];
  problems: CompileProblem[];
  renumbered: string[];
  svg: string;
  layout: ExamLayout;
}

/**
 * Replaces each inline/material image box's RAW asset — the figure the image model generated, before the
 * template drew anything on top of it — with a fresh crop of that same figure's region on the COMPILED
 * page. This matters most for a LABELLING figure: its numbered callout boxes and leader lines are drawn by
 * the typst template itself (see exam.typ's `figure-block`/`callout-mark`, and exam-images.ts's own doc
 * comment — "those numbers cannot come from the image model ..., so the template draws them"), never baked
 * into the raw asset `figureAssets` holds. Left as the raw asset, master-solution generation, grading-scheme
 * generation and grading would all be shown an unlabelled diagram with no way to tell which point is "1" vs
 * "2".
 *
 * DELIBERATELY NOT sourced from the creator's own callout metadata (the number→label pairing the creation
 * agent already knows and could just hand over as-is). Doing that would copy the creator's own answer key
 * straight into the "generated" master solution, skipping the specialised model's own read of the diagram
 * entirely. A crop of the actual printed page — the same thing a SCANNED exam's extraction already gets —
 * keeps master-solution/grading-scheme generation an honest, independent second look at what the page
 * actually shows, not a restatement of what the creator already decided.
 *
 * Runs generically over EVERY inline/material image box, not only labelling ones: there is nothing
 * task-type-specific about "trust what actually got printed over the raw input asset", and any figure could
 * in principle carry template-drawn content (a caption, a frame) the raw asset does not have.
 *
 * ALSO ADDS a box where none existed at all, for a TASK's own `inlineFigureIds` — every id in there is a
 * real figure/plot by construction (see exam-structure.ts's own `toNode`, which notes the same guarantee
 * for `noteIfDropped`), so `layout.boxes[figureId]` existing is a safe, generic signal to crop-and-attach
 * regardless of why `exam-structure.ts`'s own `boxFor` never gave it one (a plot never has a `figureAssets`
 * entry to build one from there; other figures may be missing one for other reasons). MS/GS generation and
 * grading read a task's `boundingBoxes` directly and know nothing about `inlineFigureIds` — a figure that
 * exists only as that id reference is invisible to them.
 *
 * Deliberately NOT generalized the same way for a MATERIAL's own id (`additionalInformation` below still
 * only adds a box for a known PLOT): unlike `inlineFigureIds`, a `#material(id: ...)` id is not guaranteed
 * to be a figure at all — it can wrap plain text — and `layout.boxes` records a position for any rendered
 * block, so treating "has a `layout.boxes` entry" as "is an image" here would crop a text material's own
 * region and mislabel it as a `contentType: 'image'` box.
 *
 * ALSO ADDS a `contentType: 'text'` DESCRIPTION box for EVERY task, UNCONDITIONALLY (`addDescriptionCrop`),
 * covering the task's WHOLE printed box (`layout.boxes[taskNumber]` — prompt and answer together, whatever
 * the answer's own kind is): a plain writing region (`lines`/`grid`/`blank`/...) contributes nothing beyond
 * the prompt text already sitting in `task.description`, but an `inline` one
 * (`mc`/`match`/`grid-paper`/`prompt-list`/`gap-text`) is a real visual unit — a gap-text task's
 * instructions and its gapped passage, say — that a reader should not have to reconcile two separate crops
 * to see; taking the WHOLE task box unconditionally gives every case the same one crop for free. `'text'`,
 * not `'image'` — unlike a figure, this task's full text already exists verbatim in `task.description`
 * (never OCR'd), so nothing here is more authoritative than that text; the crop only adds a picture of the
 * task's own layout alongside text that stays authoritative and editable downstream (see
 * `shouldShowOcrText`/`deriveContentType` in bounding-box-content.ts).
 *
 * INCLUDES LABEL_IMAGE, but as its ONE AND ONLY box, typed `'image'` — see `addDescriptionCrop`'s own
 * comment: its figure lives inside `body`, printed after the task's own bold heading (see `#task` in
 * exam.typ), so this whole-box crop already contains the same figure `addInlineFigureBoxIfNeeded` gave it
 * from `inlineFigureIds`, which this then removes as redundant rather than keeping both.
 *
 * One crop per figure (or per task's own description), cached — a shared MATERIAL can be referenced by
 * several tasks, and the same region must not be cropped and uploaded again for every reference to it.
 *
 * NOT defined in exam-structure.ts (deliberately): that module is filesystem/DB-free by design (see its own
 * header) for testability, and this reads image bytes and writes a new `ImageFile` per figure — squarely a
 * DB/file-touching step, so it belongs beside compile-exam.ts's other ones.
 */

type Rect = { page: number; x: number; y: number; width: number; height: number };

/** Every crop's margin on all sides — see `padded`'s own comment for why. */
const CROP_PADDING_PT = 8;

/**
 * Every crop gets a fixed margin on all sides, clamped to the page — a box captured from typst's own
 * measurement hugs its content exactly (no descender/connector-endpoint slack the way a human eye expects
 * a "screenshot" to have), so an unpadded crop reads as clipped even when it is not.
 */
function padded(rect: Rect, pageWidthPt: number, pageHeightPt: number): Rect {
  const x = Math.max(0, rect.x - CROP_PADDING_PT);
  const y = Math.max(0, rect.y - CROP_PADDING_PT);
  return {
    page: rect.page,
    x,
    y,
    width: Math.min(pageWidthPt, rect.x + rect.width + CROP_PADDING_PT) - x,
    height: Math.min(pageHeightPt, rect.y + rect.height + CROP_PADDING_PT) - y,
  };
}

/** The FULL dotted task number for a task at `parentNumber`'s depth — see `walkTasks`'s own comment. */
function fullTaskNumberOf(task: any, parentNumber: string): string {
  const own = String(task?.taskNumber ?? '');
  return parentNumber ? `${parentNumber}.${own}` : own;
}

async function attachRenderedFigureCrops(
  tasks: any[],
  additionalInformation: any[],
  layout: ExamLayout,
  figureAssets: { materialId: string; fileId: string }[],
  plotMaterialIds: ReadonlySet<string>,
  blankExamIds: string[],
): Promise<void> {
  if (blankExamIds.length === 0) return;

  const figureIdByRawFileId = new Map(figureAssets.map(asset => [asset.fileId, asset.materialId]));

  const croppedIdByCacheKey = new Map<string, string>();

  const scaleToThousand = (value: number, extentPt: number) =>
    Math.round(Math.min(1000, Math.max(0, (value / extentPt) * 1000)));

  /** One crop per `cacheKey`, cached — a region referenced more than once must not be cropped twice. */
  async function cropRect(cacheKey: string, rawRect: Rect | undefined): Promise<{ imageId: string; rect: Rect } | undefined> {
    if (!rawRect || !(rawRect.width > 0) || !(rawRect.height > 0)) return undefined;
    const rect = padded(rawRect, layout.pageWidthPt, layout.pageHeightPt);

    const cachedId = croppedIdByCacheKey.get(cacheKey);
    if (cachedId) return { imageId: cachedId, rect };

    const pageImageId = blankExamIds[Math.max(0, (rect.page || 1) - 1)];
    if (!pageImageId) return undefined;

    try {
      const cropped = await cropAndSaveRegion(pageImageId, {
        x1: scaleToThousand(rect.x, layout.pageWidthPt),
        y1: scaleToThousand(rect.y, layout.pageHeightPt),
        x2: scaleToThousand(rect.x + rect.width, layout.pageWidthPt),
        y2: scaleToThousand(rect.y + rect.height, layout.pageHeightPt),
      });
      croppedIdByCacheKey.set(cacheKey, cropped._id);
      return { imageId: cropped._id, rect };
    } catch (err) {
      logger.warn({ err, cacheKey }, 'Could not crop the rendered region');
      return undefined;
    }
  }

  function croppedCropFor(figureId: string): Promise<{ imageId: string; rect: Rect } | undefined> {
    return cropRect(figureId, layout.boxes[figureId]);
  }

  // Built as a real `BoundingBox` entity, not a plain object literal — same convention as
  // exam-structure.ts's own `boxFor` — so `_id` gets the entity's normal default (`A.getGloballyUniqueID()`)
  // instead of being silently missing. A box without one loaded fine wherever it was only ever read back
  // through code that re-derives its own id, but broke `getExamByIdQuery`'s entity hydration outright.
  function boxFromCrop(crop: { imageId: string; rect: Rect }, contentType: BoundingBoxContentType): Record<string, unknown> {
    return new BoundingBox({
      contentType,
      imageId: crop.imageId,
      ocrExtractedText: '',
      isHandwritten: false,
      confidence: 1,
      approved: true,
      pageNumber: crop.rect.page,
      topLeft: new Point({
        x: scaleToThousand(crop.rect.x, layout.pageWidthPt),
        y: scaleToThousand(crop.rect.y, layout.pageHeightPt),
      }),
      bottomRight: new Point({
        x: scaleToThousand(crop.rect.x + crop.rect.width, layout.pageWidthPt),
        y: scaleToThousand(crop.rect.y + crop.rect.height, layout.pageHeightPt),
      }),
    }).toObject();
  }

  // REPLACES a raw-asset image box (a figure the image model generated, callout-free) with the compiled
  // crop — see this function's own header comment. Returns which figureIds got fixed this way, so
  // `addInlineFigureBoxIfNeeded` below knows not to add a second box for the same figure.
  async function fixExistingImageBoxes(boxes: any[] | null | undefined): Promise<Set<string>> {
    const fixed = new Set<string>();
    if (!Array.isArray(boxes)) return fixed;
    for (const box of boxes) {
      if (box?.contentType !== 'image' || !box.imageId) continue;
      const figureId = figureIdByRawFileId.get(box.imageId);
      if (!figureId) continue;
      const crop = await croppedCropFor(figureId);
      if (!crop) continue;
      Object.assign(box, boxFromCrop(crop, 'image'));
      fixed.add(figureId);
    }
    return fixed;
  }

  /** Pushes a crop's box onto `boxesOwner`, unless one for that exact crop is already there. */
  function pushCropBox(boxesOwner: any, crop: { imageId: string; rect: Rect } | undefined, contentType: BoundingBoxContentType): void {
    if (!crop) return;
    if (!Array.isArray(boxesOwner.boundingBoxes)) boxesOwner.boundingBoxes = [];
    if (boxesOwner.boundingBoxes.some((b: any) => b?.imageId === crop.imageId)) return;
    boxesOwner.boundingBoxes.push(boxFromCrop(crop, contentType));
  }

  /**
   * ADDS a box for a figure that has no existing box to fix — the caller is responsible for only calling
   * this where `figureId` is KNOWN to be an actual figure/plot, never a possibly-text `#material` id (see
   * this function's own header comment): a task's `inlineFigureIds` (every entry a real figure/plot by
   * construction) and a known plot material's own id are the two safe callers.
   */
  async function addInlineFigureBoxIfNeeded(boxesOwner: any, figureId: string, alreadyFixed: Set<string>): Promise<void> {
    if (!figureId || alreadyFixed.has(figureId)) return;
    pushCropBox(boxesOwner, await croppedCropFor(figureId), 'image');
  }

  /**
   * See this function's own header comment. ONE crop of the task's WHOLE box (`layout.boxes[fullTaskNumber]`
   * — `fullTaskNumberOf`) for every task. LABEL_IMAGE types it `'image'` and clears any existing box first
   * (its figure box is now redundant); every other type keeps its figure box, if any, alongside this
   * `'text'` one.
   */
  async function addDescriptionCrop(task: any, fullTaskNumber: string): Promise<void> {
    const isLabelImage = task?.taskType === TaskType.LABEL_IMAGE;
    if (isLabelImage) {
      task.boundingBoxes = [];
    } else if (Array.isArray(task?.boundingBoxes)) {
      task.boundingBoxes = task.boundingBoxes.filter((b: any) => b?.contentType !== 'text');
    }
    pushCropBox(task, await croppedCropFor(fullTaskNumber), isLabelImage ? 'image' : 'text');
  }

  async function walkTasks(list: any[] | null | undefined, parentNumber = ''): Promise<void> {
    if (!Array.isArray(list)) return;
    for (const task of list) {
      const fullTaskNumber = fullTaskNumberOf(task, parentNumber);
      const fixed = await fixExistingImageBoxes(task?.boundingBoxes);
      for (const figureId of Array.isArray(task?.inlineFigureIds) ? task.inlineFigureIds : []) {
        await addInlineFigureBoxIfNeeded(task, figureId, fixed);
      }
      await addDescriptionCrop(task, fullTaskNumber);
      await walkTasks(task?.subTasks, fullTaskNumber);
    }
  }

  await walkTasks(tasks);
  for (const info of additionalInformation) {
    const fixed = await fixExistingImageBoxes(info?.boundingBoxes);
    // Only a KNOWN plot material's own id is safe to try here — a `#material(id: ...)` id can otherwise
    // be pure text (see this function's own header comment), and `AdditionalInfo._id` is the same figure
    // id `layout.boxes` uses for a plot (both come from the material's own `#material(id: ...)` block id).
    if (plotMaterialIds.has(info?._id)) await addInlineFigureBoxIfNeeded(info, info?._id, fixed);
  }
}

/**
 * Compiles, validates, and writes everything derived from the source onto the exam.
 *
 * Refuses on any `error` problem: a half-written task tree is worse than none, because the next step
 * (generation, confirmation, grading) has no way to tell a wrong tree from a right one.
 */
export async function compileAndPersistExam(
  examId: string,
  source: string,
  assets: ExamAsset[] = [],
  figureAssets: { materialId: string; fileId: string }[] = [],
  plotAssets: { materialId: string; code: string }[] = [],
  examPlanJson?: string,
  onPdfPersisted?: () => void | Promise<void>,
): Promise<PersistResult> {
  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId });
  if (!exam) throw new Error(`compileAndPersistExam: exam ${examId} not found`);

  const result = await compileExamSource(source, assets);
  const errors = result.problems.filter(problem => problem.severity === 'error');
  if (errors.length > 0) {
    throw new ExamStructureError(
      errors,
      result.problems.filter(problem => problem.severity === 'warning'),
    );
  }

  const plotMaterialIds = new Set(plotAssets.map(asset => asset.materialId));
  const existingIdsByNumber = collectTaskIds((exam as any).tasks);
  const {
    tasks: nodes,
    additionalInformation: materialPool,
    droppedFigureIds,
  } = assembleTaskNodes(result.blocks, existingIdsByNumber, figureAssets, plotMaterialIds);
  if (droppedFigureIds.length > 0) {
    // Neither a `figureAssets` entry nor a known plot — an asset that should exist but doesn't (its
    // generation most likely failed at creation time). Not fatal: the task keeps its text, just without
    // the figure MS-generation/GS-generation/grading would otherwise have seen.
    logger.warn({ examId, droppedFigureIds }, 'Some inline figures have no matching generated asset');
  }

  // `seedOwnPointsFromTotals` is off: that heuristic exists to guess what an OCR'd sheet meant. Here the
  // source states declared and own points explicitly, so there is nothing to guess.
  const tasks = await buildExamTasksFromRecursiveStructure(nodes, examId, undefined, {
    runNeedsAnswerAgent: false,
    seedOwnPointsFromTotals: false,
  });

  // Text is never OCR'd here — it is exactly what the source declared. A material's boundingBoxes carry
  // an image only when `assembleTaskNodes` matched it against `figureAssets` (a figure was generated for
  // it), so the grading/generation payload builder can find it the same way it finds an OCR'd image.
  const additionalInformation = materialPool.map(item =>
    new AdditionalInfo({ _id: item.id, content: item.content, boundingBoxes: item.boundingBoxes ?? [] }).toObject(),
  );

  // Renumbering detaches student answers, which bind by taskNumber. Reported, never silently accepted.
  const newNumbers = new Set(flattenNumbers(tasks));
  const renumbered = [...existingIdsByNumber.keys()].filter(number => !newNumbers.has(number));

  const created = await PDFFile.createFromBuffer(result.pdf, `${exam.title || 'exam'}.pdf`);
  const pdfFileId = created._id;

  // THE 90% CHECKPOINT for the exam-creator ring (chat-input.tsx's `examCreatorPercent`): the expensive
  // part (the Typst compile) is done and the PDF exists as a real, persisted file — only page-image
  // extraction and the final exam-doc write are left. Optional: only the creation-job path
  // (exam-creation-agent.ts) supplies this; exam-editor-api.ts's manual-edit path does not, and a missing
  // callback here is a deliberate no-op, not an error — this function stays oblivious to what the
  // callback does or whether it can fail (the caller is responsible for swallowing errors, same as every
  // other progress write in this feature).
  await onPdfPersisted?.();

  const blankExamIds = await extractImagesFromPDFMutation(pdfFileId)();

  // Only possible from here on — see `attachRenderedFigureCrops`'s own doc comment: it needs the compiled
  // PAGE rasters (`blankExamIds`, just generated above) to crop from, which is why this cannot run any
  // earlier in this function, right after `assembleTaskNodes` builds the geometry-less raw-asset boxes.
  await attachRenderedFigureCrops(tasks, additionalInformation, result.layout, figureAssets, plotMaterialIds, blankExamIds);

  const figureAssetEntities = figureAssets.map(asset => new ExamFigureAsset(asset).toObject());
  const plotAssetEntities = plotAssets.map(asset => new ExamPlotAsset(asset).toObject());

  await examCollection.updateOne({ _id: examId }, {
    $set: {
      tasks: tasks.map(task => (task as any).toObject?.() ?? task),
      additionalInformation,
      figureAssets: figureAssetEntities,
      plotAssets: plotAssetEntities,
      blankExamIds,
      examSource: source,
      examSourcePdfId: pdfFileId,
      examLayoutJson: JSON.stringify(result.layout),
      ...(examPlanJson ? { examPlanJson } : {}),
      ...(exam.status === 'created' ? { status: 'examTasksDefined' } : {}),
    },
  } as any);

  logger.info(
    { examId, taskCount: tasks.length, pageCount: result.pageCount, renumbered },
    'Compiled and persisted generated exam',
  );

  return {
    taskCount: tasks.length,
    pageCount: result.pageCount,
    pdfFileId,
    blankExamIds,
    problems: result.problems,
    renumbered,
    svg: result.svg,
    layout: result.layout,
  };
}

export class ExamStructureError extends Error {
  constructor(
    public readonly errors: CompileProblem[],
    public readonly warnings: CompileProblem[] = [],
  ) {
    super(`exam structure rejected:\n${errors.map(problem => `- ${problem.message}`).join('\n')}`);
    this.name = 'ExamStructureError';
  }
}

function collectTaskIds(tasks: any[] | undefined, into = new Map<string, string>()): Map<string, string> {
  for (const task of Array.isArray(tasks) ? tasks : []) {
    const number = String(task?.taskNumber ?? '').trim();
    const id = String(task?._id ?? '').trim();
    if (number && id) into.set(number, id);
    if (Array.isArray(task?.subTasks)) collectTaskIds(task.subTasks, into);
  }
  return into;
}

function flattenNumbers(tasks: any[] | undefined, into: string[] = []): string[] {
  for (const task of Array.isArray(tasks) ? tasks : []) {
    if (task?.taskNumber) into.push(String(task.taskNumber));
    if (Array.isArray(task?.subTasks)) flattenNumbers(task.subTasks, into);
  }
  return into;
}

/**
 * DEPLOYMENT:
 *
 *  1. THE COMPILER — nothing to do. `@myriaddreamin/typst-ts-node-compiler` is an ordinary dependency
 *     whose native addon arrives as a per-platform npm package with the fonts embedded, so `pnpm i`
 *     installs it and the image needs no binary, no PATH and no download step.
 *
 *  2. THE PACKAGE CACHE, still open. The template imports `@preview/mitex` (LaTeX math), which typst
 *     fetches on first use and caches under the user's cache directory. A container that starts with a
 *     cold cache reaches the network during the first creation, and in a network-restricted deployment
 *     fails there. The fix is a build-time compile of typst/warmup.typ in the Dockerfile.
 *
 * The `.typ` files are not TypeScript and are not emitted by the bundler, so the build script copies the
 * directory to `dist/server/typst` the same way it already copies `split-worker.js` by hand.
 */
